import { useEffect, useState, memo } from "react";
import styles from "./SideRail.module.css";
import Icon from "../../common/Icon";
import Tooltip, { TriggerType } from "../../common/Tooltip";
import SideNav, { SideNavItems } from "./SideNav";
import UserActInfo from "./UserActInfo";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CategoryOptions, MapUserActivity, path } from "../../common/Common";
import { useLoginStatus } from "../../../hooks/useLoginStatus";
import { ContentList } from "../../../screens/MapScreen";
import { AccountManager } from "../../../workers/AccountManager";

interface SideRailProps {
    userAct: MapUserActivity;
    overlapMapInfos?: OverlapMapInfo[];
    selectOtherMap?: (idx: number, fromButton?: boolean) => void;
    selectedMapIdx: number;
    onOpenLoginModal?: (runToolAction: () => void) => void;
    onBookmarkChange?: (isBookmark: boolean) => void;
    setContent: any;
    onClearSelectedMarker?: () => void; // 선택된 마커 해제를 위한 콜백
}

export interface OverlapMapInfo {
    bookmark: boolean | undefined;
    mapId: string;
    title: string;
    category: CategoryOptions[];
    viewCnt: number;
    bookmarkCnt: number;
    thumbCnt: number;
}

/**
 * @author L.chanwoo
 * @date 2024-08-21 17:28:47
 * @remark 사이드 레일
 * @returns
 */
const SideRail = (props: SideRailProps) => {
    const [params] = useSearchParams();
    const [navigate, location] = [useNavigate(), useLocation()];
    const isLogin = useLoginStatus();

    const [activeIdx, setActiveIdx] = useState(0);
    const [selectedOverlapIdx, setSelectedOverlapIdx] = useState(0);
    const [isProUser, setIsProUser] = useState(false);

    useEffect(() => {
        if (props.overlapMapInfos != undefined) {
            setSelectedOverlapIdx(props.overlapMapInfos.length - 1);
        }
    }, []);

    useEffect(() => {
        const accountManager = AccountManager.getInstance();
        (async () => {
            try {
                const isProUser = await accountManager.isProUser();
                setIsProUser(isProUser);
            } catch (e) {
                console.log(e);
            }
        })();
    }, []);

    useEffect(() => {
        setSelectedOverlapIdx(props.selectedMapIdx);
    }, [props.selectedMapIdx]);

    useEffect(() => {
        // 25.09.11 | SideRail 버튼 클릭으로 인한 지도 전환임을 명시
        props.selectOtherMap && props.selectOtherMap(selectedOverlapIdx, true);
    }, [selectedOverlapIdx]);

    // 지도 로고
    const navItems = [
        {
            icon: "mapAct",
            label: "지도 홈",
            disabled: false,
            onClick: () => {
                const mapId = params.get("id");
                if (mapId) {
                    navigate(`${location.pathname}?id=${mapId}`); // reset location
                    navigate(0); // reload
                }
            },
        },
    ];

    const renderItem = (items: SideNavItems[]) => {
        const buttons = items.map((item: SideNavItems, index) => {
            const thisIdx = index;
            const isActive = activeIdx === thisIdx;

            const handleClick = (e: React.MouseEvent<HTMLElement>) => {
                setActiveIdx(thisIdx);

                item.onClick && item.onClick(e);
            };

            return <SideNav key={`nav_${index}`} {...item} isActive={isActive} onClick={handleClick} />;
        });

        return buttons;
    };

    // 현재 주소를 클립보드에 저장
    const copyURL = () => {
        navigator.clipboard
            .writeText(window.location.href)
            .then(() => {
                console.log("클립보드에 복사되었습니다");
            })
            .catch((error) => {
                console.error("클립보드 복사 실패!");
            });
    };

    const overlapItem = (mapInfo: OverlapMapInfo, idx: number) => {
        const isSelected = idx === selectedOverlapIdx;
        const selectOverlapMap = () => {
            // 오버랩 지도 전환시 선택된 마커 해제
            if (props.onClearSelectedMarker) {
                props.onClearSelectedMarker();
            }
            setSelectedOverlapIdx(idx);
        };

        return (
            props.overlapMapInfos!.length > 1 && (
                <div key={idx} className={`${styles.item} ${isSelected ? styles.selected : ""}`} onClick={isSelected ? undefined : selectOverlapMap}>
                    <div className={styles.icon} style={{ backgroundColor: "blue", color: "white" }}>
                        {mapInfo.title[0]}
                    </div>
                    <div className={styles.label}>{mapInfo.title}</div>
                </div>
            )
        );
    };

    const isAddOverlapMap = () => {
        if (!props.overlapMapInfos) return false;
        if (!isLogin) return false;
        if (!isProUser && props.overlapMapInfos.length < 3) return true;
        if (isProUser && props.overlapMapInfos.length < 5) return true;
    };

    const addOverlap = () => {
        const handleAddOverlap = () => {
            props.setContent(ContentList.Overlap);
        };

        return (
            <div className={styles.item} onClick={handleAddOverlap}>
                <div className={styles.icon}>+</div>
                <div className={styles.label}>지도 추가</div>
            </div>
        );
    };

    return (
        <div className={`${styles.wrapper} undraggable`}>
            <div className={styles.top}>
                <div className={styles.logo}>
                    <Link to={path.ROOT} className={styles.link}>
                        <Icon name="logoM" color="none" size="xlarge" />
                    </Link>
                </div>
                <div>{renderItem(navItems)}</div>
                <div onClick={copyURL}>
                    <div className={styles.menu}>
                        <Tooltip message="URL이 복사되었습니다" triggerType={TriggerType.CLICK}>
                            <Icon name="shareDeAct" color="grey" />
                            <div className={styles.title}>공유</div>
                        </Tooltip>
                    </div>
                </div>
                {props.overlapMapInfos!.length > 1 && (
                    <div className={styles.overlap}>
                        {props.overlapMapInfos!.map((mapInfo, idx) => overlapItem(mapInfo, idx))}
                        {isAddOverlapMap() && addOverlap()}
                    </div>
                )}
            </div>
            <div className={styles.account}>
                {/* 로그인 여부 알 수 있는 아이콘 추가 | smyun | 250906 */}
                <Icon name="account" color={isLogin ? "default" : "lightGrey"} />

                <UserActInfo
                    data={props.userAct}
                    onBookmarkChange={props.onBookmarkChange}
                    overlapMapInfos={props.overlapMapInfos}
                    selectedMapIdx={props.selectedMapIdx}
                />
            </div>
        </div>
    );
};

export default memo(SideRail);
